"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type DevComment = {
  id: string;
  text: string;
  url: string;
  path: string;
  elementSelector: string;
  elementText: string;
  cursorX: number;
  cursorY: number;
  timestamp: string;
};

const STORAGE_KEY = "revnus-dev-comments";

function loadComments(): DevComment[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as DevComment[];
  } catch {
    return [];
  }
}

function persistComments(comments: DevComment[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(comments));
}

function buildSelector(el: Element): string {
  const parts: string[] = [];
  let current: Element | null = el;
  while (current && current !== document.body) {
    let part = current.tagName.toLowerCase();
    if (current.id) {
      part += `#${current.id}`;
      parts.unshift(part);
      break;
    }
    const cls = Array.from(current.classList)
      .filter((c) => !c.startsWith("hover:") && !c.startsWith("focus:"))
      .slice(0, 2)
      .join(".");
    if (cls) part += `.${cls}`;
    parts.unshift(part);
    current = current.parentElement;
    if (parts.length >= 4) break;
  }
  return parts.join(" > ");
}

function clampMenuPosition(x: number, y: number, menuW: number, menuH: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: x + menuW > vw - 12 ? Math.max(8, vw - menuW - 12) : x,
    y: y + menuH > vh - 12 ? Math.max(8, vh - menuH - 12) : y,
  };
}

export function DevCommentOverlay() {
  const [comments, setComments] = useState<DevComment[]>([]);
  const [menu, setMenu] = useState<{ x: number; y: number; rawX: number; rawY: number; targetEl: Element } | null>(null);
  const [commentText, setCommentText] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setComments(loadComments());
  }, []);

  // Auto-focus textarea when menu opens
  useEffect(() => {
    if (menu) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [menu]);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    const target = e.target as Element;
    if (target.closest("[data-dev-overlay]")) return;
    e.preventDefault();

    const MENU_W = 288;
    const MENU_H = 160;
    const pos = clampMenuPosition(e.clientX, e.clientY, MENU_W, MENU_H);

    setMenu({ x: pos.x, y: pos.y, rawX: e.clientX, rawY: e.clientY, targetEl: target });
    setCommentText("");
  }, []);

  useEffect(() => {
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, [handleContextMenu]);

  // Dismiss menu on outside click
  useEffect(() => {
    if (!menu) return;
    const dismiss = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest("[data-dev-menu]")) setMenu(null);
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [menu]);

  function submitComment() {
    if (!menu || !commentText.trim()) return;
    const newComment: DevComment = {
      id: crypto.randomUUID(),
      text: commentText.trim(),
      url: window.location.href,
      path: window.location.pathname + window.location.search,
      elementSelector: buildSelector(menu.targetEl),
      elementText: (menu.targetEl.textContent ?? "").trim().slice(0, 100),
      cursorX: menu.rawX,
      cursorY: menu.rawY,
      timestamp: new Date().toISOString(),
    };
    const updated = [...comments, newComment];
    setComments(updated);
    persistComments(updated);
    setMenu(null);
    setCommentText("");
  }

  function deleteComment(id: string) {
    const updated = comments.filter((c) => c.id !== id);
    setComments(updated);
    persistComments(updated);
  }

  function exportAndClear() {
    const blob = new Blob([JSON.stringify(comments, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revnus-dev-notes-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setComments([]);
    persistComments([]);
    setPanelOpen(false);
  }

  return (
    <>
      {/* ── Top-right badge ── */}
      <div data-dev-overlay className="fixed right-4 top-3 z-[9995] flex items-center gap-1">
        <button
          onClick={() => setPanelOpen((v) => !v)}
          title="Dev notes (right-click anywhere to add)"
          className="flex items-center gap-1.5 rounded-full border border-violet-300 bg-violet-700 px-3 py-1 text-xs font-semibold text-white shadow-md transition hover:bg-violet-600"
        >
          <span>✏️ Dev</span>
          {comments.length > 0 && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold leading-none text-violet-700">
              {comments.length}
            </span>
          )}
        </button>
      </div>

      {/* ── Notes panel ── */}
      {panelOpen && (
        <div
          data-dev-overlay
          className="fixed right-4 top-12 z-[9994] flex max-h-[75vh] w-[400px] flex-col overflow-hidden rounded-xl border border-violet-200 bg-white shadow-2xl"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-violet-100 bg-violet-50 px-4 py-2.5">
            <span className="text-sm font-semibold text-violet-900">Dev Notes — {comments.length}</span>
            <div className="flex items-center gap-2">
              {comments.length > 0 && (
                <button
                  onClick={exportAndClear}
                  className="rounded bg-violet-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-violet-600"
                >
                  Export JSON &amp; Clear
                </button>
              )}
              <button
                onClick={() => setPanelOpen(false)}
                className="rounded px-1.5 py-0.5 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto p-3">
            {comments.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">
                No notes yet.
                <br />
                <span className="text-xs">Right-click anywhere on the page to add one.</span>
              </p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                  <div className="flex items-start gap-2">
                    <p className="flex-1 font-medium leading-snug text-slate-800">{c.text}</p>
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="mt-0.5 shrink-0 text-slate-400 hover:text-red-500"
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                  <p className="mt-1.5 font-mono text-[10px] text-violet-600">{c.path}</p>
                  <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400">{c.elementSelector}</p>
                  {c.elementText && (
                    <p className="mt-0.5 truncate text-[10px] italic text-slate-400">near: &quot;{c.elementText}&quot;</p>
                  )}
                  <p className="mt-1 text-[10px] text-slate-300">
                    {new Date(c.timestamp).toLocaleString()} · ({c.cursorX}, {c.cursorY})
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Right-click context menu ── */}
      {menu && (
        <div
          data-dev-overlay
          data-dev-menu
          style={{ top: menu.y, left: menu.x, position: "fixed" }}
          className="z-[9999] w-72 overflow-hidden rounded-lg border border-violet-200 bg-white shadow-2xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="border-b border-violet-100 bg-violet-50 px-3 py-2">
            <p className="text-xs font-semibold text-violet-800">Add Dev Note</p>
            <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400">{buildSelector(menu.targetEl)}</p>
          </div>
          <div className="p-3">
            <textarea
              ref={textareaRef}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitComment();
                if (e.key === "Escape") setMenu(null);
              }}
              placeholder="Describe the issue or feedback..."
              className="w-full resize-none rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
              rows={3}
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[10px] text-slate-400">Ctrl/⌘ + Enter to save · Esc to cancel</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setMenu(null)}
                  className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={submitComment}
                  disabled={!commentText.trim()}
                  className="rounded bg-violet-700 px-2 py-1 text-xs font-semibold text-white hover:bg-violet-600 disabled:opacity-40"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
